// hooks/useDivingReports.js
import { useState, useMemo } from "react";

// Helper function for consistent date formatting
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const useDivingReports = (
  certifications,
  recentDives,
  userObjectId,
  apiClient
) => {
  const [shareUrl, setShareUrl] = useState("");
  const [shareLoading, setShareLoading] = useState(false);

  const generateShareableLink = async () => {
    if (!userObjectId) {
      alert("User ID not available for sharing");
      return;
    }
    if (!apiClient) {
      alert("API client not available. Cannot generate share link.");
      return;
    }

    try {
      setShareLoading(true);

      const response = await apiClient.post("/api/credentials/share", {
        diverId: userObjectId,
        certifications,
        recentDives,
      });

      const { slug } = response.data;
      const url = `${window.location.origin}/credentials/${slug}`;
      setShareUrl(url);

      // Copy to clipboard
      await navigator.clipboard.writeText(url);
      alert("Shareable link copied to clipboard!");
    } catch (error) {
      console.error("Error generating share link:", error);
      if (error.response?.status === 401) {
        alert("Authentication failed. Please log in again.");
      } else if (error.response?.status === 403) {
        alert("Access denied. You don't have permission to share credentials.");
      } else {
        alert("Failed to generate shareable link. Please try again.");
      }
    } finally {
      setShareLoading(false);
    }
  };

  // Memoized function to get unique dive locations for the print preview
  const getUniqueLocations = useMemo(() => {
    const locations = recentDives
      .map((dive) => {
        const city =
          dive.divesite?.uddf?.divesite?.site?.geography?.address?.city;
        const province =
          dive.divesite?.uddf?.divesite?.site?.geography?.address?.province;
        const country =
          dive.divesite?.uddf?.divesite?.site?.geography?.address?.country;

        const parts = [];
        if (city) parts.push(city);
        if (province) parts.push(province);
        if (country) parts.push(country);

        return parts.length > 0 ? parts.join(", ") : "Unknown Location";
      })
      .filter(Boolean); // Filter out any empty strings or nulls

    // Return a function so that it can be called later within the component
    // without re-calculating the Set on every render unless recentDives changes.
    return () => [...new Set(locations)];
  }, [recentDives]); // Recalculate only when recentDives changes

  // Memoized data structure for grouped dives (site + date)
  const groupedDives = useMemo(() => {
    const groups = {};
    recentDives.forEach((dive) => {
      const siteName =
        dive.divesite?.uddf?.divesite?.site?.name || "Unknown Site";
      const diveDate = formatDate(dive.diveDate); // Use the consistent formatDate helper

      // Create a unique key for grouping by site and date
      const key = `${siteName}-${diveDate}`;

      if (!groups[key]) {
        groups[key] = {
          siteName: siteName,
          date: diveDate,
          location: {
            city: dive.divesite?.uddf?.divesite?.site?.geography?.address?.city,
            province:
              dive.divesite?.uddf?.divesite?.site?.geography?.address?.province,
            country:
              dive.divesite?.uddf?.divesite?.site?.geography?.address?.country,
          },
          count: 0, // Initialize count for this group
        };
      }
      groups[key].count++; // Increment dive count for this group
    });

    // Convert the groups object into a sorted array for rendering
    return Object.values(groups).sort(
      (a, b) => new Date(b.date) - new Date(a.date) // Sort by date descending
    );
  }, [recentDives]); // Recalculate only when recentDives changes

  return {
    shareUrl,
    shareLoading,
    generateShareableLink,
    getUniqueLocations,
    groupedDives,
    formatDate, // Expose formatDate for UI to use consistently
  };
};
